import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export default function VisaTypeManager({ api, getAuthHeader }) {
  const [visaTypes, setVisaTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({
    visa_type: '',
    price_aed: 0,
    stay_days: 0,
    validity_days: 0,
    processing_time: '',
    requirements: '',
    notes: ''
  });

  useEffect(() => {
    fetchVisaTypes();
  }, []);

  const fetchVisaTypes = async () => {
    try {
      const response = await axios.get(`${api}/visa-types`);
      setVisaTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch visa types');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${api}/visa-types/${editingId}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Visa type updated successfully');
      } else {
        await axios.post(`${api}/visa-types`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Visa type added successfully');
      }
      resetForm();
      fetchVisaTypes();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleEdit = (visaType) => {
    setFormData({
      visa_type: visaType.visa_type,
      price_aed: visaType.price_aed,
      stay_days: visaType.stay_days,
      validity_days: visaType.validity_days,
      processing_time: visaType.processing_time,
      requirements: visaType.requirements,
      notes: visaType.notes
    });
    setEditingId(visaType.id);
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this visa type?')) return;
    try {
      await axios.delete(`${api}/visa-types/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Visa type deleted successfully');
      fetchVisaTypes();
    } catch (error) {
      toast.error('Failed to delete visa type');
    }
  };

  const resetForm = () => {
    setFormData({
      visa_type: '',
      price_aed: 0,
      stay_days: 0,
      validity_days: 0,
      processing_time: '',
      requirements: '',
      notes: ''
    });
    setEditingId(null);
    setShowAdd(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Manage Visa Types</h2>
          <p className="text-slate-600 mt-1">Total: {visaTypes.length}</p>
        </div>
        <Button
          data-testid="add-visa-type-btn"
          onClick={() => setShowAdd(!showAdd)}
          className="btn-primary flex items-center gap-2"
        >
          {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showAdd ? 'Cancel' : 'Add Visa Type'}
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Visa Type' : 'Add New Visa Type'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="label-text">Visa Type Name *</label>
                  <Input
                    data-testid="visa-type-name-input"
                    value={formData.visa_type}
                    onChange={(e) => setFormData({ ...formData, visa_type: e.target.value })}
                    className="input-field"
                    placeholder="e.g., UAE Tourist Visa 30 Days"
                    required
                  />
                </div>
                <div>
                  <label className="label-text">Price (AED) *</label>
                  <Input
                    data-testid="visa-type-price-input"
                    type="number"
                    step="0.01"
                    value={formData.price_aed}
                    onChange={(e) => setFormData({ ...formData, price_aed: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="label-text">Stay Days *</label>
                  <Input
                    data-testid="visa-type-stay-days-input"
                    type="number"
                    value={formData.stay_days}
                    onChange={(e) => setFormData({ ...formData, stay_days: parseInt(e.target.value) || 0 })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="label-text">Validity Days *</label>
                  <Input
                    data-testid="visa-type-validity-days-input"
                    type="number"
                    value={formData.validity_days}
                    onChange={(e) => setFormData({ ...formData, validity_days: parseInt(e.target.value) || 0 })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="label-text">Processing Time *</label>
                  <Input
                    data-testid="visa-type-processing-time-input"
                    value={formData.processing_time}
                    onChange={(e) => setFormData({ ...formData, processing_time: e.target.value })}
                    className="input-field"
                    placeholder="e.g., 3-5 working days"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="label-text">Requirements</label>
                <Textarea
                  data-testid="visa-type-requirements-input"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  className="input-field min-h-[100px]"
                  placeholder="Enter visa requirements"
                />
              </div>
              <div>
                <label className="label-text">Notes</label>
                <Textarea
                  data-testid="visa-type-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field min-h-[80px]"
                  placeholder="Additional notes"
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="btn-primary flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {editingId ? 'Update' : 'Add'}
                </Button>
                <Button type="button" onClick={resetForm} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <div className="grid gap-4">
        {visaTypes.map((visaType) => (
          <Card key={visaType.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-bold text-slate-900">{visaType.visa_type}</h3>
                    <span className="px-4 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                      AED {visaType.price_aed}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-slate-500 font-semibold uppercase">Stay Duration</p>
                      <p className="text-lg font-semibold text-slate-700">{visaType.stay_days} days</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-semibold uppercase">Validity</p>
                      <p className="text-lg font-semibold text-slate-700">{visaType.validity_days} days</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500 font-semibold uppercase">Processing Time</p>
                      <p className="text-lg font-semibold text-slate-700">{visaType.processing_time}</p>
                    </div>
                  </div>
                  {visaType.requirements && (
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Requirements:</strong> {visaType.requirements}
                    </p>
                  )}
                  {visaType.notes && (
                    <p className="text-sm text-slate-600">
                      <strong>Notes:</strong> {visaType.notes}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-visa-type-${visaType.id}`}
                    onClick={() => handleEdit(visaType)}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-visa-type-${visaType.id}`}
                    onClick={() => handleDelete(visaType.id)}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
